/**
 * WelcomeScreen — Step 0
 *
 * Centred layout with:
 *  - App logo + name heading
 *  - Personalised greeting
 *  - Subtitle / purpose + time estimate
 *  - 2-column step preview cards
 *  - "Let's Start ->" CTA
 *  - "Skip onboarding" plain text link
 */

import {
  Building2,
  TrendingUp,
  FolderKanban,
  Wallet,
  Users,
  FileText,
  Bell,
  Plug,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface StepCard {
  icon: React.ElementType;
  name: string;
  description: string;
}

const STEP_CARDS: StepCard[] = [
  {
    icon: Plug,
    name: 'Integrations',
    description: 'Connect your finance, time, expense and purchasing systems first.',
  },
  {
    icon: Building2,
    name: 'Company Setup',
    description: 'Your organisation name, logo, address and bank details.',
  },
  {
    icon: TrendingUp,
    name: 'Finance & Reporting',
    description: 'Set your GP calculation method and revenue display mode.',
  },
  {
    icon: FolderKanban,
    name: 'Project Configuration',
    description: 'Project creation rules, key people roles and custom fields.',
  },
  {
    icon: Wallet,
    name: 'Budget Settings',
    description: 'Choose your budget mode — a one-time decision that shapes all projects.',
  },
  {
    icon: Users,
    name: 'User Permissions',
    description: 'Assign platform roles and access levels to your team.',
  },
  {
    icon: FileText,
    name: 'Templates',
    description: 'Set up your invoice and email templates ready for billing.',
  },
  {
    icon: Bell,
    name: 'Notifications',
    description: 'Configure WIP alerts, billing schedules and utilisation warnings.',
  },
];

interface WelcomeScreenProps {
  firstName: string;
  onStart: () => void;
  onSkip: () => void;
}

export function WelcomeScreen({ firstName, onStart, onSkip }: WelcomeScreenProps) {
  return (
    <div className="min-h-full flex items-start justify-center px-8 py-10">
      <div className="w-full max-w-3xl space-y-8">
        {/* Logo + Heading */}
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="h-14 w-14 rounded-2xl bg-primary flex items-center justify-center shadow-md">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-foreground">Projects</h1>
          <p className="text-2xl">
            Welcome, {firstName}! 👋
          </p>
          <p className="text-muted-foreground max-w-xl leading-relaxed">
            Let's get your workspace set up. This wizard will guide you through
            the key configuration steps — it only takes about 10 minutes and you
            can always come back to it later.
          </p>
        </div>

        {/* Step preview cards — 2 column grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {STEP_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.name}
                className={cn(
                  'flex items-start gap-3 rounded-lg border border-border bg-card p-4',
                  'hover:border-primary/40 hover:bg-primary/5 transition-colors',
                )}
              >
                <div className="h-9 w-9 flex-shrink-0 rounded-md bg-primary/10 flex items-center justify-center">
                  <Icon className="h-4.5 w-4.5 text-primary h-[18px] w-[18px]" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">{card.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    {card.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA */}
        <div className="flex flex-col items-center gap-3">
          <Button size="lg" className="px-10" onClick={onStart}>
            Let's Start →
          </Button>
          <button
            type="button"
            className="text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline transition-colors"
            onClick={onSkip}
          >
            Skip onboarding
          </button>
        </div>
      </div>
    </div>
  );
}
